import React, { useState, useEffect, useRef } from 'react'
import classNames from 'classnames'
import { usePathname } from 'next/navigation'
import { MaxWidthGlobal } from '@/ui/layout/Gutter/new/MaxWidthGlobal'
import { MegaMenuProps } from './types'
import { CategoryList } from './CategoryList'
import { LinkList } from './LinkList'
import { DetailPanel } from './DetailPanel'
import { MobileMegaMenu } from './MobileMegaMenu'
import { useTranslations } from 'next-intl'
import { NavItem } from '@/ui/layout/Header/NavBar/NavItem'
import { createPortal } from 'react-dom'
import { removeLangFromPath } from '@/utils/removeLangFromPath'

// Main MegaMenu Component
export const MegaMenu: React.FC<MegaMenuProps> = ({
  className: classNameFromProps,
  data,
  trigger,
  isMobile = false,
  onOpenChange,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [hoveredCategoryIndex, setHoveredCategoryIndex] = useState<number | null>(null)
  const [hoveredLinkIndex, setHoveredLinkIndex] = useState<number | null>(null)
  const [activeCategoryIndex, setActiveCategoryIndex] = useState<number | null>(null)
  const [mobileView, setMobileView] = useState<'categories' | 'links'>('categories')
  const [currentVisited, setCurrentVisited] = useState<{
    categoryIndex: number
    linkIndex: number | null
  } | null>(null)

  const menuRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const pathName = usePathname()

  const t = useTranslations('megaMenu')

  useEffect(() => {
    closeMenu()
    let activeCatIndex = -1
    let activeLinkIndex = -1
    for (let catIndex = 0; catIndex < data.categories.length; catIndex++) {
      const category = data.categories[catIndex]
      if (category) {
        activeLinkIndex = category.links.findIndex((link) => {
          const linkParts = removeLangFromPath(link.href).split('/')
          const pathParts = removeLangFromPath(pathName).split('/')

          if (linkParts.at(1) === 'areas' && !link.isComingSoon) {
            if (linkParts.at(2) === pathParts.at(2)) {
              activeCatIndex = catIndex
              return true
            }
          }
          return false
        })
      }

      if (activeLinkIndex != -1) {
        break
      }
    }

    setCurrentVisited({
      categoryIndex: activeCatIndex,
      linkIndex: activeLinkIndex,
    })
  }, [pathName])

  useEffect(() => {
    if (currentVisited && currentVisited?.categoryIndex !== -1) {
      setHoveredCategoryIndex(currentVisited.categoryIndex)
    }

    if (currentVisited && currentVisited?.linkIndex !== -1) {
      setHoveredLinkIndex(currentVisited.linkIndex)
    } else {
      if (hoveredCategoryIndex !== null && (hoveredLinkIndex === null || hoveredLinkIndex === -1)) {
        const hoveredCategory = data.categories[hoveredCategoryIndex]
        if (hoveredCategory) {
          setHoveredLinkIndex(hoveredCategory.links.findIndex((link) => !link.isComingSoon))
        }
      }
    }
  }, [currentVisited, isOpen])

  useEffect(() => {
    if (isOpen && !isMobile && data.categories.length > 0) {
      if (hoveredCategoryIndex === null || hoveredCategoryIndex === -1) {
        setHoveredCategoryIndex(0)
        setFirstAvailableLinkIndex(0)
      }
    }
  }, [isOpen, isMobile, hoveredCategoryIndex])

  const setFirstAvailableLinkIndex = (catIndex: number) => {
    const activeCategory = data.categories[catIndex]
    if (activeCategory)
      setHoveredLinkIndex(activeCategory.links.findIndex((item) => !item.isComingSoon) ?? 0)
  }

  // Set initial hover states when menu opens
  useEffect(() => {
    if (!isMobile) {
    }
  }, [isOpen, isMobile, data.categories, hoveredCategoryIndex])

  const toggle = () => {
    const newOpenState = !isOpen
    setIsOpen(newOpenState)
    if (onOpenChange) {
      onOpenChange(newOpenState)
    }
    if (isMobile) {
      setMobileView('categories')
      setActiveCategoryIndex(null)
    }
  }

  const closeMenu = () => {
    if (onOpenChange) {
      onOpenChange(false)
    }
    setIsOpen(false)
    setMobileView('categories')
    // onClose && onClose()
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        closeMenu()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [onOpenChange])

  // Effect to set the first category as active on desktop hover

  const getHoveredLink = () => {
    if (hoveredCategoryIndex === null || hoveredLinkIndex === null) return null
    return data.categories[hoveredCategoryIndex]?.links[hoveredLinkIndex] || null
  }

  // Clone the trigger element with the isOpen prop
  const triggerElement = trigger
    ? React.cloneElement(
        trigger as React.ReactElement,
        {
          isActive: isOpen,
          'aria-expanded': isOpen,
        } as any,
      )
    : null

  // Render overlay with portal to document body
  const renderOverlay = () => {
    if (isMobile || !isOpen || typeof document === 'undefined') return null

    return createPortal(
      <div
        className="fixed backdrop-blur-sm inset-0 bottom-0 bg-black bg-opacity-30 animate-in fade-in duration-300 ease-out"
        onClick={closeMenu}
        style={{ zIndex: 40 }}
      />,
      document.body,
    )
  }

  return (
    <div
      ref={menuRef}
      className={classNames(`relative ${isMobile && 'w-full'}`, classNameFromProps)}
    >
      <div
        onClick={(e) => {
          e.stopPropagation()
          toggle()
        }}
      >
        {triggerElement || trigger}
      </div>

      {renderOverlay()}

      {isOpen && (
        <div
          className={classNames('fixed z-50', {
            'p-0 top-[80px] mt-1 left-1/2 transform -translate-x-1/2 inset-x-auto mx-auto w-auto lg:max-w-screen-2xl max-w-screen-md':
              !isMobile,
            'fixed h-dvh inset-0 z-50 flex flex-col pt-24 bg-gradient-to-r from-[#5250B5] to-[#224383] lg:min-h-80 max-h-[600px] px-4 animate-in fade-in duration-300 ease-out':
              isMobile,
          })}
        >
          {!isMobile && (
            <div className="bg-bg-base-transparent-95 w-full h-full p-2 backdrop-blur shadow-sm rounded-md animate-in zoom-in-95 slide-in-from-top-2 duration-300 ease-out origin-top">
              {/* Mobile header - Always visible when menu is open on mobile */}
              {isMobile && (
                <div className="flex items-center justify-between mb-6 border-b border-border-subtle-transparent-10">
                  {/* Conditional Title/Back Button */}
                  {mobileView === 'links' &&
                  typeof activeCategoryIndex === 'number' &&
                  data.categories[activeCategoryIndex] ? (
                    <NavItem
                      showBackIcon={true}
                      label={t('areas')}
                      onClick={() => setMobileView('categories')}
                      isMobile={true}
                    />
                  ) : (
                    <NavItem
                      showBackIcon={true}
                      label={t('back')}
                      onClick={closeMenu}
                      isMobile={true}
                    />
                  )}
                </div>
              )}

              {/* Main Content Area */}
              <div
                ref={contentRef}
                className={classNames('flex', {
                  'flex-col overflow-y-auto w-full': isMobile,
                  'h-auto': !isMobile,
                })}
              >
                {!isMobile ? (
                  <MaxWidthGlobal className="flex w-auto">
                    <CategoryList
                      item={data}
                      hoveredIndex={hoveredCategoryIndex}
                      onHover={(index) => {
                        setHoveredCategoryIndex(index)
                        if (index != currentVisited?.categoryIndex) {
                          setFirstAvailableLinkIndex(index)
                        } else {
                          setHoveredLinkIndex(currentVisited?.linkIndex)
                        }
                      }}
                      isMobile={isMobile}
                    />
                    {hoveredCategoryIndex !== null && (
                      <div className="flex flex-row justify-between animate-in fade-in-0">
                        <LinkList
                          category={data.categories[hoveredCategoryIndex]}
                          hoveredLinkIndex={hoveredLinkIndex}
                          setHoveredLinkIndex={setHoveredLinkIndex}
                          onLinkClick={closeMenu}
                          pathName={pathName}
                        />
                        <DetailPanel
                          link={getHoveredLink()}
                          onLinkClick={closeMenu}
                          currentPath={pathName}
                        />
                      </div>
                    )}
                  </MaxWidthGlobal>
                ) : (
                  <MobileMegaMenu
                    data={data}
                    activeCategoryIndex={activeCategoryIndex}
                    setActiveCategoryIndex={setActiveCategoryIndex}
                    mobileView={mobileView}
                    setMobileView={setMobileView}
                    currentPath={pathName}
                  />
                )}
              </div>
            </div>
          )}

          {isMobile && (
            <>
              {/* Mobile header - Always visible when menu is open on mobile */}
              <div className="flex items-center justify-between mb-6 border-b border-border-subtle-transparent-10">
                {/* Conditional Title/Back Button */}
                {mobileView === 'links' &&
                typeof activeCategoryIndex === 'number' &&
                data.categories[activeCategoryIndex] ? (
                  <NavItem
                    showBackIcon={true}
                    label={t('areas')}
                    onClick={() => setMobileView('categories')}
                    isMobile={true}
                  />
                ) : (
                  <NavItem
                    showBackIcon={true}
                    label={t('back')}
                    onClick={closeMenu}
                    isMobile={true}
                  />
                )}
              </div>

              {/* Main Content Area */}
              <div
                ref={contentRef}
                className={classNames('flex animate-in slide-in-from-right duration-300 ease-out', {
                  'flex-col overflow-y-auto w-full': isMobile,
                  'h-auto': !isMobile,
                })}
              >
                <MobileMegaMenu
                  data={data}
                  activeCategoryIndex={activeCategoryIndex}
                  setActiveCategoryIndex={setActiveCategoryIndex}
                  mobileView={mobileView}
                  setMobileView={setMobileView}
                  currentPath={pathName}
                />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}
